import { AlertTriangle } from 'lucide-react';
import { useState, type ReactElement } from 'react';

import { StatePanel } from '@/components/ui/StatePanel';
import { useToast } from '@/components/ui/toast';

import { sampleTraceLine, settingsErrorCopy } from '../presentation';
import type { WatermarkSettings } from '../types';
import { useUpdateWatermarkSettings, useWatermarkSettings } from '../use-watermark';
import {
  draftBody,
  draftErrors,
  draftFrom,
  isDirty,
  previewStyle,
  type WatermarkDraft,
} from '../watermark-form';

import { WatermarkConfigCard } from './WatermarkConfigCard';
import { WatermarkPreview } from './WatermarkPreview';
import { skeletonLine } from '@/components/ui/skeleton';
import { errorAdvice } from '@/lib/error-advice';

interface Props {
  companyId: string;
  companyName: string;
  userName: string;
  canEdit: boolean;
}

/** Forma de la vista previa + configuración mientras carga (UX-LOAD-01). */
export function WatermarkPanelSkeleton({ label }: { label: string }): ReactElement {
  return (
    <div className="g-84" aria-busy="true" aria-label={label}>
      <div className="wmdoc wmdoc-loading" />
      <div className="card">
        <div className="cb">
          {Array.from({ length: 6 }, (_, i) => (
            <div key={i} className={skeletonLine({ width: 90, space: 'y' })} />
          ))}
        </div>
      </div>
    </div>
  );
}

/** Vista previa + configuración (diseño `.g-84` del mockup). El borrador vive acá para
 *  que la vista previa refleje cada cambio antes de guardar. */
export function WatermarkPanel(props: Props): ReactElement {
  const query = useWatermarkSettings({ companyId: props.companyId, enabled: true });
  if (query.isLoading) {
    return <WatermarkPanelSkeleton label="Cargando configuración" />;
  }
  if (query.isError || !query.data) {
    return (
      <StatePanel
        icon={<AlertTriangle />}
        title="No se pudo cargar la configuración"
        tone="danger"
        role="alert"
        onRetry={() => void query.refetch()}
        retrying={query.isRefetching}
      >
        {errorAdvice(query.error)}
      </StatePanel>
    );
  }
  // Remonta al cambiar la configuración guardada: el borrador parte de lo vigente.
  return <Editor key={JSON.stringify(query.data)} settings={query.data} {...props} />;
}

function Editor({
  settings,
  companyName,
  userName,
  canEdit,
}: Props & { settings: WatermarkSettings }): ReactElement {
  const [draft, setDraft] = useState<WatermarkDraft>(() => draftFrom(settings));
  const update = useUpdateWatermarkSettings();
  const { showToast } = useToast();
  const errors = draftErrors(draft);
  const valid = Object.keys(errors).length === 0;

  const save = (): void => {
    if (!valid || update.isPending) {
      return;
    }
    update.mutate(draftBody(draft, settings), {
      onSuccess: () => showToast('Configuración de marca de agua guardada'),
    });
  };

  return (
    <div className="g-84">
      <WatermarkPreview
        legends={{ interna: draft.interna.text, externa: draft.externa.text }}
        style={previewStyle(draft, settings)}
        traceLine={sampleTraceLine(companyName, userName, new Date())}
      />
      <WatermarkConfigCard
        draft={draft}
        errors={errors}
        canEdit={canEdit}
        canSave={valid && isDirty(draft, settings)}
        saving={update.isPending}
        saveError={update.isError ? settingsErrorCopy(update.error) : null}
        onChange={(patch) => setDraft((d) => ({ ...d, ...patch }))}
        onSave={save}
      />
    </div>
  );
}
